import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { YearCalendar, type YearCalendarEntry } from '../../../../components/calendar/YearCalendar';
import { maintenanceItemsApi } from '../buildingManagementApi';
import type { MaintenanceCalendarFilters } from '../buildingManagementTypes';
import { maintenanceItemTitle } from '../utils/maintenanceItemTitle';
import { entryDot, entryState } from './maintenanceCalendarDots';

/**
 * The register's CALENDAR view — the shared `<YearCalendar>`, fed every date an item falls due.
 *
 * **An item's CURRENT due date carries its aging band; every later occurrence is its recurrence
 * projected forward and reads On Schedule.** A monthly service is therefore in all twelve
 * columns, which is the point: the year's workload, month by month.
 */
export function MaintenanceYearCalendar({ filters, reloadKey }: { filters: MaintenanceCalendarFilters; reloadKey: number }) {
  const navigate = useNavigate();

  /*
    The shell hands a NEW object every render, so the fetch keys on what the filters SAY rather
    than on their identity — or every render would refetch the year.
  */
  const filterKey = JSON.stringify(filters);

  const load = useCallback(
    (from: string, to: string, signal: AbortSignal) =>
      maintenanceItemsApi
        .calendar(from, to, JSON.parse(filterKey) as MaintenanceCalendarFilters, signal)
        .then((entries) =>
          entries.map((entry): YearCalendarEntry => {
            const title = maintenanceItemTitle(entry);
            const state = entryState(entry);

            return {
              key: `${entry.maintenanceItemId}-${entry.date}`,
              date: entry.date,
              dot: entryDot(entry),
              title,
              tooltip: `${entry.referenceNumber} — ${title} · ${state}`,
              detail: `, ${entry.referenceNumber}, ${state}`,
              onOpen: () => navigate(`/general-services/building-management/maintenance/${entry.maintenanceItemId}`),
            };
          }),
        ),
    [filterKey, navigate],
  );

  return (
    <YearCalendar
      id="building-management-calendar"
      load={load}
      reloadKey={reloadKey}
      summary={(count, year) => `${count} due ${count === 1 ? 'date' : 'dates'} in ${year}.`}
    />
  );
}
